/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { ProgressStepper, ProgressStep } from '@patternfly/react-core';
import InProgressIcon from '@patternfly/react-icons/dist/esm/icons/in-progress-icon';
import { Session } from 'types/liveClass';

type Props = {
  /** ISO start (e.g. "2025-09-22T12:45:00+00:00") */
  startTime: string;
  /** ISO end (e.g. "2025-10-03T16:00:00+00:00") */
  endTime: string;
  /** IANA time zone (e.g. "America/New_York") */
  timezone: string;
  /** Explicit sessions; when provided (non-empty), weekends/holidays are allowed */
  sessions?: Session[];
};

export default function ClassDaysStepper({
  startTime,
  endTime,
  timezone,
  sessions,
}: Props) {
  const { t } = useTranslation();

  // "Today" in the class time zone, normalized to day start
  const today = React.useMemo(
    () => DateTime.now().setZone(timezone).startOf('day'),
    [timezone],
  );

  // Normalize start/end to midnight of their local dates (for fallback path)
  const startDay = React.useMemo(
    () => DateTime.fromISO(startTime, { zone: timezone }).startOf('day'),
    [startTime, timezone],
  );
  const endDay = React.useMemo(
    () => DateTime.fromISO(endTime, { zone: timezone }).startOf('day'),
    [endTime, timezone],
  );

  const useSessions = Array.isArray(sessions) && sessions.length > 0;

  // Build steps from explicit sessions (if provided),
  // else Mon–Fri between start/end (inclusive)
  const steps = React.useMemo(() => {
    if (useSessions) {
      const parsed = sessions!
        .map((s) => {
          const start = DateTime.fromISO(s.startDate, { zone: timezone });
          const end = DateTime.fromISO(s.endDate, { zone: timezone });
          return { raw: s, start, end, day: start.startOf('day') };
        })
        .filter(
          ({ start, end }) => start.isValid && end.isValid && !(end < start),
        )
        .sort((a, b) => a.start.toMillis() - b.start.toMillis());

      return parsed.map(({ raw, day }, i) => {
        const isPast = day < today;
        const isCurrent = day.hasSame(today, 'day');
        return {
          id: `session-${i + 1}`,
          label: raw.sessionName || `${t('Day')} ${i + 1}`,
          variant: isPast ? 'success' : isCurrent ? 'pending' : 'default',
          isCurrent,
        } as const;
      });
    }

    // Fallback: Mon–Fri only between startDay and endDay (inclusive)
    if (!startDay.isValid || !endDay.isValid || startDay > endDay) return [];

    const days: DateTime[] = [];
    let cur = startDay;

    while (cur <= endDay) {
      const isEndpoint =
        cur.hasSame(startDay, 'day') || cur.hasSame(endDay, 'day');
      const isWeekday = cur.weekday <= 5; // 1=Mon … 7=Sun

      if (isEndpoint || isWeekday) {
        days.push(cur);
      }
      cur = cur.plus({ days: 1 });
    }

    return days.map((d, i) => {
      const isPast = d < today;
      const isCurrent = d.hasSame(today, 'day');
      return {
        id: `day-${i + 1}`,
        label: `${t('Day')} ${i + 1}`,
        variant: isPast ? 'success' : isCurrent ? 'pending' : 'default',
        isCurrent,
      } as const;
    });
  }, [useSessions, sessions, timezone, t, startDay, endDay, today]);

  return (
    <ProgressStepper aria-label="Class progress">
      {steps.map((s, idx) => (
        <ProgressStep
          key={idx}
          id={s.id}
          titleId={`${s.id}-title`}
          variant={s.variant}
          isCurrent={s.isCurrent}
          icon={s.isCurrent ? <InProgressIcon /> : undefined}
          aria-label={`${s.label} step`}
        >
          {s.label}
        </ProgressStep>
      ))}
    </ProgressStepper>
  );
}
